import React, {Component} from 'react'
import {Box, Button, Grid, Switch, TextField, Tooltip} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {makeColoredSwitch} from "components/helpers/colored.switch";
import {ReactGrid} from 'components/helpers/reactGrid/reactGrid';
import ClientesService from 'services/clientes.service';
import {withSnackbar} from "notistack";
import {MainContext} from "contexts/main.context";
import SubHeader from "components/helpers/subHeader/subHeader";
import PersonAddIcon from "@material-ui/icons/PersonAddRounded";
import {sortNumeric} from "assets/utils";
import {RoomRounded, BlurOnRounded} from "@material-ui/icons";
import {ClientesMapDialog} from "components/clientes/list/clientes.map.dialog";
import {ExportOptions} from "components/helpers/exportOptions/export.options";
import {ListadosService} from "services";

// Colores con los que se pintan las filas según el estado del cliente.
const ROW_COLORS = {
    inhabilitado: 'rgba(255,84,84,0.22)',
    inactivo: 'rgba(255,227,84,0.22)',
    prospecto: 'rgba(84,170,255,0.22)'
};

// Versión sólida de los mismos colores, para teñir los switches que sirven de referencia.
const SWITCH_COLORS = {
    inhabilitado: 'rgb(255,84,84, 0.5)',
    inactivo: 'rgb(255,227,84, 0.5)',
    prospecto: 'rgb(84,170,255, 0.5)'
};

const InhabilitadoSwitch = makeColoredSwitch(SWITCH_COLORS.inhabilitado);
const InactivoSwitch = makeColoredSwitch(SWITCH_COLORS.inactivo);
const ProspectoSwitch = makeColoredSwitch(SWITCH_COLORS.prospecto);

export class ClientesList extends Component {
    ClientesService = new ClientesService(this.context);
    ListadosService = new ListadosService(this.context);

    state = {
        columns: [
            {name: 'id', title: 'ID'},
            {name: 'razsoc', title: 'Razón Social', style: {whiteSpace: 'break-spaces'}},
            {name: 'cuit', title: 'CUIT'},
            {name: 'localidad', title: 'Localidad'},
            {name: 'direccion', title: 'Dirección', style: {whiteSpace: 'break-spaces'}},
            {name: 'apenom', title: 'Vendedor'},
            {name: 'clasifica', title: 'Clasificación'}
        ],
        columnExtensions: [
            {columnName: 'id', width: 115},
            {columnName: 'cuit', compare: sortNumeric, width: 105},
            {columnName: 'localidad', width: 170},
            {columnName: 'apenom', width: 140},
            {columnName: 'clasifica', width: 127},
        ],
        rowExtensions: {
            color: cliente => cliente.inhabilitado ? ROW_COLORS.inhabilitado : cliente.inactivo ? ROW_COLORS.inactivo : cliente.id[0] === 'X' ? ROW_COLORS.prospecto : ''
        },
        direcciones: [],
        direcciones_filtered: [],
        clientes: [],
        rows: [],
        vendedores: [],
        mapDialogOpen: false,
        inhabilitados: false,
        inactivos: false,
        comunes: true,
        prospectos: true,
        vendedoresFiltrados: []
    };

    filteredRows = [];

    componentDidMount() {
        this.ClientesService.getAllWithDirecciones(
            response => {
                const direcciones = response.data.result;
                let clientes = direcciones.reduce((clientes, direccion) => {
                    (clientes[direccion['id']] = clientes[direccion['id']] || []).push(direccion);
                    return clientes;
                }, {});
                clientes = Object.keys(clientes).map(idcliente => clientes[idcliente][0]);

                this.setState(prevState => ({
                    ...prevState,
                    direcciones: direcciones,
                    clientes: clientes,
                    rows: clientes,
                    vendedores: this.getVendedores(clientes)
                }), this.filterClientes)
            },
            error => this.props.enqueueSnackbar(error, {variant: 'error'}));
    }

    // Vendedores presentes en el listado, para las opciones del filtro.
    // Los clientes que tienen cargado un vendedor que ya no existe en el maestro llegan
    // sin apenom (el LEFT JOIN no matchea) y no se ofrecen como opción: se los lista con
    // NiquelAPI/scripts/vendedores_huerfanos.php.
    getVendedores = clientes => {
        const vendedores = clientes.reduce((vendedores, cliente) => {
            if (cliente.vendedor && cliente.apenom && !vendedores[cliente.vendedor])
                vendedores[cliente.vendedor] = cliente.apenom;
            return vendedores;
        }, {});

        return Object.keys(vendedores)
            .map(id => ({id: id, name: vendedores[id]}))
            .sort((a, b) => a.name.localeCompare(b.name));
    };

    filterClientes = () => {
        this.setState(prevState => {
            // Sin vendedores seleccionados no se filtra por vendedor (equivale a "todos").
            const vendedores = prevState.vendedoresFiltrados.map(vendedor => vendedor.id);

            return {
                ...prevState,
                rows: prevState.clientes.filter(cliente => {
                    return !(
                        (cliente.inhabilitado && !prevState.inhabilitados) ||
                        (cliente.inactivo && !prevState.inactivos) ||
                        ((cliente.id[0] === 'X') && !prevState.prospectos) ||
                        ((cliente.id[0] !== 'X') && !cliente.inhabilitado && !cliente.inactivo && !prevState.comunes) ||
                        (vendedores.length > 0 && !vendedores.includes(cliente.vendedor))
                    )
                }),
                direcciones_filtered: prevState.direcciones.filter(direccion => {
                    return !(
                        (direccion.inhabilitado && !prevState.inhabilitados) ||
                        (direccion.inactivo && !prevState.inactivos) ||
                        ((direccion.id[0] === 'X') && !prevState.prospectos) ||
                        ((direccion.id[0] !== 'X') && !direccion.inhabilitado && !direccion.inactivo && !prevState.comunes) ||
                        (vendedores.length > 0 && !vendedores.includes(direccion.vendedor))
                    )
                })
            };
        });
    }

    navCreate = (row) => {
        this.props.history.push(`${this.props.match.url}/create`);
    };

    // Quien no puede editar entra a la misma pantalla en modo solo lectura.
    navEdit = row => {
        this.props.history.push(`${this.props.match.url}/${this.canEdit() ? 'edit' : 'view'}/${row.id}`);
    };

    // Solo administradores y operadores pueden editar clientes; supervisores y vendedores solo verlos.
    canEdit = () => ["A", "O"].includes(this.context.loggedUser.role);
    canView = () => ["A", "O", "S", "V"].includes(this.context.loggedUser.role);

    setMapDialog = open => this.setState({mapDialogOpen: open});

    setInhabilitados = inabilitados => this.setState({inhabilitados: inabilitados}, this.filterClientes);
    setInactivos = inactivos => this.setState({inactivos: inactivos}, this.filterClientes);
    setComunes = comunes => this.setState({comunes: comunes}, this.filterClientes);
    setProspectos = prospectos => this.setState({prospectos: prospectos}, this.filterClientes);
    setVendedoresFiltrados = vendedores => this.setState({vendedoresFiltrados: vendedores}, this.filterClientes);

    onFilterRows = _filteredRows => this.filteredRows = _filteredRows;

    getPDF = fnSuccess => {
        this.ListadosService.clientes(
            this.filteredRows.map(row => row.id),
            file => fnSuccess(file),
            error => this.props.enqueueSnackbar(error, {variant: 'error'})
        )
    };

    getExcel = fnSuccess => {
        this.ListadosService.clientesExcel(
            this.filteredRows.map(row => row.id),
            file => fnSuccess(file),
            error => this.props.enqueueSnackbar(error, {variant: 'error'})
        )
    };

    render() {
        const {
            columns, columnExtensions, rowExtensions, rows, mapDialogOpen, direcciones_filtered,
            inhabilitados, inactivos, comunes, prospectos, vendedores, vendedoresFiltrados
        } = this.state;

        return (
            <Box className='niquel-clientes-list'>
                <SubHeader
                    title={'Clientes'}
                    subtitle={'Listado de clientes'}
                />
                <Box className='container-xl mt-3 mb-3'>
                    <Grid container spacing={2}>
                        <Grid item className={'mr-3 border-right'}>
                            Comunes
                            <Switch
                                checked={comunes}
                                onChange={() => this.setComunes(!comunes)}
                                name="checkedB"
                                color="secondary"
                            />
                        </Grid>
                        <Grid item>
                            Inhabilitados
                            <InhabilitadoSwitch
                                checked={inhabilitados}
                                onChange={() => this.setInhabilitados(!inhabilitados)}
                                name="checkedB"
                            />
                        </Grid>
                        <Grid item>
                            Inactivos
                            <InactivoSwitch
                                checked={inactivos}
                                onChange={() => this.setInactivos(!inactivos)}
                                name="checkedB"
                            />
                        </Grid>
                        <Grid item className={'border-right'}>
                            Prospectos
                            <ProspectoSwitch
                                checked={prospectos}
                                onChange={() => this.setProspectos(!prospectos)}
                                name="checkedB"
                            />
                        </Grid>
                        <Grid item className={'ml-2'}>
                            <Tooltip title={'Agregar cliente prospecto'}>
                                <Button
                                    color="primary"
                                    onClick={this.navCreate}
                                    variant={"outlined"}
                                >
                                    <PersonAddIcon/> Cliente Prospecto
                                </Button>
                            </Tooltip>
                        </Grid>
                        <Grid item>
                            <Tooltip title={'Ver en mapa'}>
                                <Button
                                    color="textSecondary"
                                    onClick={() => this.setMapDialog(true)}
                                    variant={"outlined"}
                                >
                                    <RoomRounded/> Ven en Mapa
                                </Button>
                            </Tooltip>
                        </Grid>
                        {/* Los vendedores no filtran por vendedor; los supervisores sí. */}
                        {this.context.loggedUser.role !== 'V' && (
                            <Grid item style={{minWidth: 320}}>
                                <Autocomplete
                                    multiple
                                    fullWidth
                                    disableCloseOnSelect
                                    filterSelectedOptions
                                    limitTags={2}
                                    size={'small'}
                                    options={vendedores}
                                    value={vendedoresFiltrados}
                                    onChange={(event, value) => this.setVendedoresFiltrados(value)}
                                    getOptionLabel={option => option.name}
                                    getOptionSelected={(option, value) => option.id === value.id}
                                    renderInput={params => (
                                        <TextField {...params} label="Vendedores" placeholder={vendedoresFiltrados.length ? '' : 'Todos'}/>
                                    )}
                                />
                            </Grid>
                        )}
                    </Grid>
                    <Box className='mt-3'>
                        <ReactGrid
                            title={'Clientes'}
                            showResultCount
                            height={-240}
                            columns={columns}
                            columnExtensions={columnExtensions}
                            rowExtensions={rowExtensions}
                            rows={rows}
                            enableFiltering={false}
                            // showFilters={false}
                            rowClick={false}
                            enableEdition={this.canView()}
                            editionViewOnly={!this.canEdit()}
                            onEdit={this.navEdit}
                            onFilterRows={this.onFilterRows}
                        />
                    </Box>
                    {["A", "O", "S"].includes(this.context.loggedUser.role) && (
                        <ExportOptions
                            enableDownload
                            enableMail
                            enablePrint
                            enableShow
                            enableExcel
                            fabIcon={<BlurOnRounded/>}
                            getPDF={this.getPDF}
                            getExcel={this.getExcel}
                            fileName={"clientes.pdf"}
                            excelFileName={"clientes.xlsx"}
                            mailSubject={"Clientes"}
                            mailBody={"Estimado,  \n\nSe adjunta el listado de clientes del sistema."}
                        />
                    )}
                </Box>
                <ClientesMapDialog
                    open={mapDialogOpen}
                    fullScreen
                    direcciones={direcciones_filtered}
                    onCancel={() => this.setMapDialog(false)}
                />
            </Box>
        )
    }
}

ClientesList.contextType = MainContext;
ClientesList = withSnackbar(ClientesList);
